import { useGaugeState } from "@mui/x-charts"

import type { ColorGradient } from "types/gaugePlot.types"
import { ANGULAR_OVERSHOOT_DEG, interpolateColor, N_SEGMENTS } from "./helpers"

const SEAM_OVERLAP = 0.015

interface MuiGaugeColoredArcProps {
  colorGradient: ColorGradient[]
  actualValue: number
  domainMin: number
  domainMax: number
  startAngleRad: number
  endAngleRad: number
}

const MuiGaugeColoredArc = ({
  colorGradient,
  actualValue,
  domainMin,
  domainMax,
  startAngleRad,
  endAngleRad,
}: MuiGaugeColoredArcProps) => {
  const { innerRadius, outerRadius, cx, cy } = useGaugeState()

  const meanRadius = (innerRadius + outerRadius) / 2
  const arcWidth = outerRadius - innerRadius
  const overshootRad = (ANGULAR_OVERSHOOT_DEG * Math.PI) / 180
  const arcSpan = endAngleRad - startAngleRad
  const valueRatio =
    domainMax > domainMin
      ? Math.max(0, Math.min(1, (actualValue - domainMin) / (domainMax - domainMin)))
      : 0

  const drawStartAngle = startAngleRad - overshootRad
  const drawEndAngle = startAngleRad + valueRatio * arcSpan + overshootRad
  const totalDraw = drawEndAngle - drawStartAngle

  const point = (angle: number) => ({
    x: cx + meanRadius * Math.sin(angle),
    y: cy - meanRadius * Math.cos(angle),
  })

  const segments = Array.from({ length: N_SEGMENTS }, (_, i) => {
    const a1 = drawStartAngle + (i / N_SEGMENTS) * totalDraw
    const a2 =
      drawStartAngle +
      ((i + 1) / N_SEGMENTS) * totalDraw +
      (i < N_SEGMENTS - 1 ? SEAM_OVERLAP : 0)
    const t = ((i + 0.5) / N_SEGMENTS) * totalDraw - overshootRad
    return {
      p1: point(a1),
      p2: point(a2),
      color: interpolateColor(colorGradient, t / arcSpan),
    }
  })

  return (
    <g>
      {segments.map(({ p1, p2, color }, i) => (
        <path
          key={i}
          d={`M ${p1.x} ${p1.y} A ${meanRadius} ${meanRadius} 0 0 1 ${p2.x} ${p2.y}`}
          fill="none"
          stroke={color}
          strokeWidth={arcWidth}
          strokeLinecap={i === 0 || i === N_SEGMENTS - 1 ? "round" : "butt"}
        />
      ))}
    </g>
  )
}

export default MuiGaugeColoredArc
